import type { Journey, Run, RunLeg } from '@/types/journey'

/**
 * Journey arithmetic for **display only**.
 *
 * Deliberately not a second timeline. The API computes the real one — from the
 * same normative vectors MapTap tests against — and ships it on the run as
 * `legs`, so anything that has been started reads its numbers from there. What
 * is left is the one thing the web needs *before* a run exists: roughly how far
 * and how long the journey somebody just clicked on is. Distance and a sum, and
 * nothing that could quietly disagree with playback.
 */

const METRES_PER_DEGREE = 111_320
const DEFAULT_SPEED_MPS = 1.3
const DEFAULT_TRANSITION_S = 20

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const positiveOr = (value: unknown, fallback: number) =>
  finite(value) && value > 0 ? value : fallback

// `transition_s: 0` means "no minimum, just walk it", so zero is kept.
const nonNegativeOr = (value: unknown, fallback: number) =>
  finite(value) && value >= 0 ? value : fallback

/**
 * Equirectangular metres, with the `cos` term on the **mean** latitude. Same
 * formula as `api/src/journey/timeline.rs`; dropping the mean is the porting
 * mistake the contract calls out by name.
 */
export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const meanLatRad = (((a.lat + b.lat) / 2) * Math.PI) / 180
  const dy = (b.lat - a.lat) * METRES_PER_DEGREE
  const dx = (b.lng - a.lng) * METRES_PER_DEGREE * Math.cos(meanLatRad)
  return Math.hypot(dx, dy)
}

export interface JourneySummary {
  distanceM: number
  durationS: number
  levels: number[]
}

/** Length and duration of a journey nobody has started yet. */
export function summarise(journey: Journey): JourneySummary {
  const waypoints = journey.waypoints ?? []
  const journeySpeed = positiveOr(journey.speed_mps, DEFAULT_SPEED_MPS)

  let distanceM = 0
  let durationS = 0
  const levels = new Set<number>()

  for (let i = 0; i < waypoints.length; i++) {
    const here = waypoints[i]
    if (finite(here?.level)) levels.add(here.level)

    durationS += nonNegativeOr(here?.dwell_s, 0)

    const next = waypoints[i + 1]
    if (!next) continue

    const legDistance = distanceMeters(here, next)
    const speed = positiveOr(next.speed_mps, journeySpeed)
    let legDuration = legDistance / speed
    if (here.level !== next.level) {
      legDuration = Math.max(legDuration, nonNegativeOr(next.transition_s, DEFAULT_TRANSITION_S))
    }
    distanceM += legDistance
    durationS += legDuration
  }

  return { distanceM, durationS, levels: [...levels].sort((a, b) => a - b) }
}

/** "2 min 05 s" / "48 s". Short enough for a sidebar row. */
export function formatDuration(seconds: number): string {
  if (!finite(seconds) || seconds < 0) return '—'
  const whole = Math.round(seconds)
  if (whole < 60) return `${whole} s`
  const minutes = Math.floor(whole / 60)
  const rest = whole % 60
  if (minutes < 60) return `${minutes} min ${String(rest).padStart(2, '0')} s`
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`
}

export function formatDistance(metres: number): string {
  if (!finite(metres)) return '—'
  return metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(2)} km`
}

export interface RunProgress {
  /** Journey seconds elapsed, clamped to the run's duration. */
  elapsedS: number
  /** 0–1, for the bar. */
  ratio: number
  leg: RunLeg | null
  legIndex: number
  /** Seconds left of the dwell being stood in, or null while walking. */
  dwellingRemainingS: number | null
  level: number | null
}

/**
 * Where a run is now, from what the last `run` event said.
 *
 * The server sends an event per *transition*, not per tick — a per-second
 * message would clone the users map and re-run three `setData` calls in every
 * open tab — so progress is extrapolated here from `started_at`, the journey
 * time already consumed, and the speed multiplier.
 */
export function progressOf(run: Run, now: number): RunProgress {
  const consumed = run.paused_elapsed_s
  const sinceStart =
    run.state === 'running' ? Math.max(0, (now - new Date(run.started_at).getTime()) / 1000) : 0

  let elapsedS = consumed + sinceStart * run.speed
  if (run.duration_s > 0) {
    // A looping run wraps rather than sitting at the end; the server does the
    // same thing and simply does not send an event for it.
    elapsedS = run.loop ? elapsedS % run.duration_s : Math.min(elapsedS, run.duration_s)
  } else {
    elapsedS = 0
  }

  let legIndex = -1
  let leg: RunLeg | null = null
  for (let i = 0; i < run.legs.length; i++) {
    const candidate = run.legs[i]
    if (elapsedS < candidate.start_s + candidate.duration_s) {
      legIndex = i
      leg = candidate
      break
    }
  }
  if (!leg && run.legs.length > 0) {
    legIndex = run.legs.length - 1
    leg = run.legs[legIndex]
  }

  const dwellingRemainingS =
    leg && leg.kind === 'dwell' ? Math.max(0, leg.start_s + leg.duration_s - elapsedS) : null

  return {
    elapsedS,
    ratio: run.duration_s > 0 ? Math.min(1, elapsedS / run.duration_s) : 0,
    leg,
    legIndex,
    dwellingRemainingS,
    level: leg ? leg.level : null,
  }
}

/** Where each waypoint falls along the progress bar, as a 0–1 fraction. */
export function waypointTicks(run: Run): number[] {
  if (run.duration_s <= 0) return []
  const ticks = new Set<number>()
  for (const leg of run.legs) {
    if (leg.kind !== 'walk') continue
    ticks.add(Math.min(1, (leg.start_s + leg.duration_s) / run.duration_s))
  }
  return [...ticks].filter((tick) => tick > 0 && tick < 1).sort((a, b) => a - b)
}

/** The label of the waypoint a run is currently at, if it has one. */
export function labelAt(journey: Journey | null, leg: RunLeg | null): string | null {
  if (!journey || !leg) return null
  return journey.waypoints?.[leg.from]?.label ?? null
}

/**
 * The live user a run's positions show up as.
 *
 * A preview ingests as `journey:<run id>`; a relay run is the leased tag as the
 * loopback brings it back, `blueiot:<tag id>` — the dot is what the customer's
 * app also received, not what was meant to be sent.
 */
export function walkerUserId(run: Run): string | null {
  if (run.sink === 'relay') return run.tag_id ? `blueiot:${run.tag_id}` : null
  return `journey:${run.id}`
}

/**
 * A wristband number, readable: `900 000 000 024`.
 *
 * Digits are grouped in threes from the right with a narrow no-break space
 * (U+202F), so the number never wraps mid-group and a copy still pastes as one
 * token. **Display only** — what is sent to the API and what the phone is
 * configured with is the bare string. Anything that is not all digits is
 * returned untouched rather than guessed at.
 */
export function formatTagId(tagId: string | null | undefined): string {
  if (!tagId) return '—'
  if (!/^\d+$/.test(tagId)) return tagId
  return tagId.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}
